import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "href", "to", "visuallyHiddenText", "onClick", "sameTab", "aria-label", "disabled"];
import { VisuallyHidden } from '@figure/frontlib-accessibility';
import React from 'react';
import styled from 'styled-components';
import { Link as BaseLink } from 'react-router-dom';
import { combineCss } from '../../styling';
import { A, StyledButton } from '../Styled';
import { toAlphanumericDashString } from '../../utils';

var isString = function isString(el) {
  return typeof el === 'string';
};

var Link = /*#__PURE__*/styled(BaseLink).withConfig({
  displayName: "LinkOrButton__Link",
  componentId: "sc-wci5ax-0"
})(["", ""], combineCss());
/**
 * For `<a>` links, provide either `href` or `to`. If neither `href` or `to` is provided, it will wrap a `<button>`.
 * @link https://reactrouter.com/web/api/Link Refer to react-router <Link> on what to pass to "to" prop.
 * @returns A wrapper for internal / external links and buttons.
 * A11Y: A space is added when there is both visible text and visually hidden text to improve screen reader output.
 * A11Y: The `disabled` attribute is not valid on links. We remove the href, add a role, and use aria-disabled to disable a link.
 * See Disabling a link (https://www.scottohara.me/blog/2021/05/28/disabled-links.html) for more details.
 */

var LinkOrButton = function LinkOrButton(_ref) {
  var children = _ref.children,
      href = _ref.href,
      to = _ref.to,
      visuallyHiddenText = _ref.visuallyHiddenText,
      onClick = _ref.onClick,
      _ref$sameTab = _ref.sameTab,
      sameTab = _ref$sameTab === void 0 ? false : _ref$sameTab,
      ariaLabel = _ref['aria-label'],
      disabled = _ref.disabled,
      rest = _objectWithoutProperties(_ref, _excluded);

  var dataTestId;

  if (isString(children)) {
    dataTestId = toAlphanumericDashString(children);
  } else if (Array.isArray(children) && children.every(isString)) {
    dataTestId = toAlphanumericDashString(children.join(''));
  } else if (ariaLabel) {
    dataTestId = toAlphanumericDashString(ariaLabel);
  } else if (!dataTestId && visuallyHiddenText) {
    // Visually hidden text is not always preceded by visible text
    dataTestId = toAlphanumericDashString(visuallyHiddenText);
  } // Always use visually hidden text instead of aria-label


  var visuallyHiddenDisplayText = visuallyHiddenText || ariaLabel;

  if (href) {
    // TODO khigh why are the a and Link elements giving type errors when putting the as prop before ...rest?
    // TODO: The disabled attribute is invalid on a link.
    // TODO: The disabled Link is announced as disabled but still focusable. Fix when working on React Router update.
    return /*#__PURE__*/React.createElement(A, _extends({
      href: disabled ? undefined : href // A11Y
      ,
      onClick: onClick,
      target: sameTab ? undefined : '_blank',
      rel: "noopener noreferrer",
      "data-test-id": dataTestId,
      role: disabled ? 'link' : undefined // A11Y
      ,
      "aria-disabled": disabled ? true : undefined // A11Y
      ,
      disabled: disabled
    }, rest), children, (!sameTab || visuallyHiddenDisplayText) && /*#__PURE__*/React.createElement(VisuallyHidden, null, children && ' ', visuallyHiddenDisplayText || 'Opens a new window'));
  }

  if (to) {
    return /*#__PURE__*/React.createElement(Link, _extends({
      disabled: disabled && 'disabled',
      to: to,
      onClick: onClick,
      "data-test-id": dataTestId,
      "aria-disabled": disabled ? true : undefined // A11Y

    }, rest), children, visuallyHiddenDisplayText && /*#__PURE__*/React.createElement(VisuallyHidden, null, children && ' ', visuallyHiddenDisplayText));
  }

  return /*#__PURE__*/React.createElement(StyledButton, _extends({
    disabled: disabled,
    onClick: onClick,
    "data-test-id": dataTestId,
    type: "button"
  }, rest), children, visuallyHiddenDisplayText && /*#__PURE__*/React.createElement(VisuallyHidden, null, children && ' ', visuallyHiddenDisplayText));
};

export default LinkOrButton;